import { Col, Row, Card, message, Spin, Badge, Calendar } from "antd";
import React, { useEffect, useState, useCallback, useMemo } from "react";
import MotionHoc from "../../../utils/MotionHoc";
import dayjs from "dayjs";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import SaleAppointmentData from "./modal/SaleAppointmentData";

dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

/* ------------------------------------------------------------------ */
/* MOCK วันนัดประมูลขาย — แต่ละ Lot มีตารางนัด (ไม่เกิน 6 นัด)          */
/* อิงจาก "วันนี้" เพื่อให้มีทั้งนัดที่ผ่านมาแล้ว และกำลังจะมาถึง         */
/* ------------------------------------------------------------------ */
const today = dayjs();

// helper: สร้าง Lot พร้อมตารางนัดจาก array ของ day-offset
const mkLot = (
  contno,
  lotNo,
  customerName,
  province,
  offsets,
  results = {},
) => ({
  contno,
  lotNo,
  customerName,
  province,
  saleSchedule: offsets.map((off, i) => ({
    round: i + 1,
    date: today
      .add(off, "day")
      .hour(10)
      .minute(0)
      .second(0)
      .millisecond(0)
      .format("YYYY-MM-DDTHH:mm:ss"),
  })),
  saleResults: results,
});

const SALE_LOTS = [
  mkLot(
    "2-0012446",
    "A-01",
    "หจก. ทีเอ ทรานสปอร์ต (2017)",
    "หนองคาย",
    [-25, -5, 10, 40, 70, 100],
    {
      1: { resultType: 2, price: null, reason: "ไม่มีผู้เข้าสู้ราคา" },
      2: { resultType: 2, price: null, reason: "ไม่มีผู้เข้าสู้ราคา" },
    },
  ),
  mkLot(
    "2-0011000",
    "B-01",
    "นายสมชาย รุ่งเรือง",
    "เชียงใหม่",
    [-12, 10, 35, 65, 95, 125],
    { 1: { resultType: 2, price: null, reason: "งดการขาย" } },
  ),
  mkLot(
    "1-0009999",
    "C-01",
    "นางวิภา ทองคำ",
    "นนทบุรี",
    [3, 33, 63, 93, 123, 153],
  ),
  mkLot(
    "7-0000022",
    "D-01",
    "นายองอาจ คัมภีระยศ",
    "นครราชสีมา",
    [-2, 28, 58, 88, 118, 148],
    { 1: { resultType: 1, price: 850000, reason: "" } },
  ),
];

// flatten: 1 event = 1 นัดของ 1 Lot
const SALE_EVENTS = SALE_LOTS.flatMap((lot) =>
  lot.saleSchedule
    .filter((s) => s.date)
    .map((s) => ({
      eventId: `${lot.contno}-${lot.lotNo}-${s.round}`,
      contno: lot.contno,
      lotNo: lot.lotNo,
      customerName: lot.customerName,
      province: lot.province,
      round: s.round,
      date: s.date,
      result: lot.saleResults?.[s.round] || null,
    })),
);

const Main = () => {
  // ============== STATES ==============
  const [dataArr, setDataArr] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isModal, setIsModal] = useState(false);
  const [dataModal, setDataModal] = useState([]);
  const [dateSelect, setDateSelect] = useState("");
  const [panelMode, setPanelMode] = useState("month");
  const [currentDate, setCurrentDate] = useState(dayjs());

  // ============== COLORS ==============
  const COLORS = {
    upcoming: "#ff4d4f", // แดง = กำลังจะมาถึง
    completed: "#52c41a", // เขียว = ผ่านมาแล้ว
    default: "#1890ff", // น้ำเงิน = รวม
  };

  // ============== LOAD DATA (mock) ==============
  useEffect(() => {
    const sorted = [...SALE_EVENTS].sort(
      (a, b) => dayjs(a.date).valueOf() - dayjs(b.date).valueOf(),
    );
    setDataArr(sorted);
    // TODO: ต่อ API จริง -> แปลงให้เป็น event รายนัดในรูปแบบเดียวกัน
  }, []);

  // ============== HELPERS ==============
  const isUpcoming = useCallback(
    (d) => d && dayjs(d).isAfter(dayjs(), "day"),
    [],
  );
  const isCompleted = useCallback(
    (d) => d && dayjs(d).isBefore(dayjs(), "day"),
    [],
  );

  const getListData = useCallback(
    (value) => dataArr.filter((item) => dayjs(item.date).isSame(value, "day")),
    [dataArr],
  );

  const getMonthData = useCallback(
    (value) => {
      const monthData = dataArr.filter((item) => {
        const date = dayjs(item.date);
        return date.year() === value.year() && date.month() === value.month();
      });
      return {
        total: monthData.length,
        upcoming: monthData.filter((i) => isUpcoming(i.date)).length,
        completed: monthData.filter((i) => isCompleted(i.date)).length,
      };
    },
    [dataArr, isUpcoming, isCompleted],
  );

  const eventColor = useCallback(
    (d) => (isUpcoming(d) ? COLORS.upcoming : COLORS.completed),
    [isUpcoming],
  );

  // ============== CELL RENDER ==============
  const monthCellRender = useCallback(
    (value) => {
      const { total, upcoming, completed } = getMonthData(value);
      if (total === 0) return null;
      return (
        <div style={{ textAlign: "center", paddingTop: 8 }}>
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              gap: 4,
              flexWrap: "wrap",
            }}
          >
            {upcoming > 0 && (
              <Badge
                count={upcoming}
                style={{ backgroundColor: COLORS.upcoming }}
                overflowCount={99}
              />
            )}
            {completed > 0 && (
              <Badge
                count={completed}
                style={{ backgroundColor: COLORS.completed }}
                overflowCount={99}
              />
            )}
          </div>
        </div>
      );
    },
    [getMonthData],
  );

  const dateCellRender = useCallback(
    (value) => {
      const listData = getListData(value);
      const count = listData.length;
      if (count === 0) return null;

      const upcomingCount = listData.filter((i) => isUpcoming(i.date)).length;
      const completedCount = listData.filter((i) => isCompleted(i.date)).length;

      return (
        <div style={{ fontSize: "12px" }}>
          <div
            style={{
              textAlign: "center",
              marginBottom: 4,
              display: "flex",
              justifyContent: "center",
              gap: 4,
              flexWrap: "wrap",
            }}
          >
            {upcomingCount > 0 && (
              <Badge
                count={upcomingCount}
                style={{ backgroundColor: COLORS.upcoming }}
                overflowCount={99}
                title="นัดประมูลที่กำลังจะมาถึง"
              />
            )}
            {completedCount > 0 && (
              <Badge
                count={completedCount}
                style={{ backgroundColor: COLORS.completed }}
                overflowCount={99}
                title="นัดประมูลที่ผ่านมาแล้ว"
              />
            )}
          </div>

          <ul
            className="events"
            style={{ padding: 0, margin: 0, listStyle: "none" }}
          >
            {listData.slice(0, 2).map((item) => (
              <li key={item.eventId} style={{ marginBottom: 2 }}>
                <Badge
                  color={eventColor(item.date)}
                  text={
                    <div
                      style={{
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        fontSize: "11px",
                      }}
                    >
                      <div style={{ fontWeight: "bold" }}>{item.contno}</div>
                      <div style={{ color: "#666" }}>
                        นัดที่ {item.round} · {dayjs(item.date).format("HH:mm")}{" "}
                        น.
                      </div>
                    </div>
                  }
                />
              </li>
            ))}
            {count > 2 && (
              <li
                style={{
                  textAlign: "center",
                  color: "#1890ff",
                  fontSize: "11px",
                }}
              >
                +{count - 2} รายการ
              </li>
            )}
          </ul>
        </div>
      );
    },
    [getListData, isUpcoming, isCompleted, eventColor],
  );

  const cellRender = useCallback(
    (current, info) => {
      if (info.type === "date") return dateCellRender(current);
      if (info.type === "month") return monthCellRender(current);
      return info.originNode;
    },
    [dateCellRender, monthCellRender],
  );

  // ============== HANDLERS ==============
  const handlePanelChange = useCallback((value, mode) => {
    setPanelMode(mode);
    setCurrentDate(value);
  }, []);

  const handleDateSelect = useCallback(
    (date) => {
      if (panelMode === "year") {
        setPanelMode("month");
        setCurrentDate(date);
        return;
      }
      if (panelMode === "month") {
        const selectedList = dataArr.filter((item) =>
          dayjs(item.date).isSame(date, "day"),
        );
        if (selectedList.length > 0) {
          setDateSelect(date.format("YYYY-MM-DD"));
          setDataModal(selectedList);
          setIsModal(true);
        } else {
          message.info("ไม่มีนัดประมูลในวันที่เลือก");
        }
      }
    },
    [panelMode, dataArr],
  );

  const handleCloseModal = useCallback(() => {
    setIsModal(false);
    setDataModal([]);
    setDateSelect("");
  }, []);

  // ============== SUMMARY ==============
  const totalAppointments = dataArr.length;
  const upcomingAppointments = useMemo(
    () => dataArr.filter((i) => isUpcoming(i.date)).length,
    [dataArr, isUpcoming],
  );
  const completedAppointments = useMemo(
    () => dataArr.filter((i) => isCompleted(i.date)).length,
    [dataArr, isCompleted],
  );

  // ============== RENDER ==============
  return (
    <>
      <Card
        title={
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 8,
            }}
          >
            <span style={{ fontSize: "18px", fontWeight: "bold" }}>
              ปฏิทินนัดประมูลขายทอดตลาด
            </span>
            <div
              style={{
                fontSize: "14px",
                display: "flex",
                alignItems: "center",
                gap: 16,
                flexWrap: "wrap",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <Badge
                  count={totalAppointments}
                  style={{ backgroundColor: COLORS.default }}
                  overflowCount={999}
                />
                <span style={{ color: "#666" }}>นัดทั้งหมด</span>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <Badge
                  count={upcomingAppointments}
                  style={{ backgroundColor: COLORS.upcoming }}
                  overflowCount={999}
                />
                <span style={{ color: "#666" }}>กำลังจะมาถึง</span>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <Badge
                  count={completedAppointments}
                  style={{ backgroundColor: COLORS.completed }}
                  overflowCount={999}
                />
                <span style={{ color: "#666" }}>ผ่านมาแล้ว</span>
              </div>
            </div>
          </div>
        }
      >
        <Spin spinning={loading} size="large" tip="กำลังโหลดข้อมูล...">
          <Row>
            <Col span={24}>
              <Card
                size="small"
                style={{
                  marginBottom: 16,
                  background: `rgba(0, 57, 96, 0.08)`,
                  border: `1px solid rgba(0, 57, 96, 0.15)`,
                }}
              >
                <div style={{ fontSize: "13px", color: "#003960" }}>
                  💡 <strong>วิธีใช้:</strong>
                  {panelMode === "year" ? (
                    <span style={{ marginLeft: 8 }}>
                      คลิกที่<strong>เดือน</strong>เพื่อดูปฏิทินของเดือนนั้น
                    </span>
                  ) : (
                    <span style={{ marginLeft: 8 }}>
                      คลิกที่<strong>วันที่</strong>
                      เพื่อดูรายละเอียดนัดประมูลขายในวันนั้น
                    </span>
                  )}
                  <span style={{ marginLeft: 16 }}>
                    |
                    <span
                      style={{
                        marginLeft: 8,
                        color: COLORS.upcoming,
                        fontWeight: "bold",
                      }}
                    >
                      ● แดง
                    </span>{" "}
                    = กำลังจะมาถึง
                    <span
                      style={{
                        marginLeft: 8,
                        color: COLORS.completed,
                        fontWeight: "bold",
                      }}
                    >
                      ● เขียว
                    </span>{" "}
                    = ผ่านมาแล้ว
                  </span>
                </div>
              </Card>

              <Calendar
                value={currentDate}
                cellRender={cellRender}
                onSelect={handleDateSelect}
                onPanelChange={handlePanelChange}
                mode={panelMode}
              />
            </Col>
          </Row>
        </Spin>
      </Card>

      {isModal && (
        <SaleAppointmentData
          open={isModal}
          close={handleCloseModal}
          dataRec={dataModal}
          date={dateSelect}
        />
      )}
    </>
  );
};

const SaleAnnouncementCalendar = MotionHoc(Main);
export default SaleAnnouncementCalendar;
